import React, { forwardRef } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { singleLineClamp } from '@jira/platform__single-line-clamp-mixin';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { summary_softwareReleasesVersionDetailIssueList_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/summary_softwareReleasesVersionDetailIssueList_jiraIssue.graphql';
import type { summary_softwareReleasesVersionDetailIssueList_jiraIssueSummaryField$key } from '@atlassian/jira-relay/src/__generated__/summary_softwareReleasesVersionDetailIssueList_jiraIssueSummaryField.graphql';
import type { summary_softwareReleasesVersionDetailIssueList_jiraIssueTypeField$key } from '@atlassian/jira-relay/src/__generated__/summary_softwareReleasesVersionDetailIssueList_jiraIssueTypeField.graphql';
import IssueTypeAvatarFallbackUrl from './assets/issue-type-avatar-fallback.svg';

const TooltipTag = forwardRef((props, ref) => <IssueSummary {...props} ref={ref} />);

type Props = {
	jiraIssue: summary_softwareReleasesVersionDetailIssueList_jiraIssue$key;
	jiraIssueSummaryField: summary_softwareReleasesVersionDetailIssueList_jiraIssueSummaryField$key;
	jiraIssueTypeField: summary_softwareReleasesVersionDetailIssueList_jiraIssueTypeField$key;
};

export const Summary = ({
	jiraIssue,
	jiraIssueSummaryField,
	jiraIssueTypeField,
}: Props): React.JSX.Element => {
	const { key } = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment summary_softwareReleasesVersionDetailIssueList_jiraIssue on JiraIssue {
				key @required(action: THROW)
			}
		`,
		jiraIssue,
	);

	const { text: summary } = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment summary_softwareReleasesVersionDetailIssueList_jiraIssueSummaryField on JiraSingleLineTextField {
				text @required(action: THROW)
			}
		`,
		jiraIssueSummaryField,
	);

	const { issueType } = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment summary_softwareReleasesVersionDetailIssueList_jiraIssueTypeField on JiraIssueTypeField {
				issueType @required(action: THROW) {
					name @required(action: THROW)
					avatar {
						xsmall @required(action: LOG)
					}
				}
			}
		`,
		jiraIssueTypeField,
	);

	return (
		<SummaryContainer>
			<Icon
				src={issueType.avatar?.xsmall ?? IssueTypeAvatarFallbackUrl}
				alt={issueType.name}
				title={issueType.name}
			/>
			<IssueKey>{key}</IssueKey>
			{/* @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<RefAttributes<unknown>>' is not assignable to type 'keyof IntrinsicElements | ComponentType<AllHTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement>; }> | undefined'. */}
			<Tooltip content={summary} tag={TooltipTag}>
				<IssueSummary>{summary}</IssueSummary>
			</Tooltip>
		</SummaryContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SummaryContainer = styled.div({
	display: 'flex',
	flex: 1,
	height: '100%',
	minWidth: '200px',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > :not(:last-child)': {
		marginRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	height: '16px',
	width: '16px',
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: 0,
	flex: '0 0 auto',
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueSummary = styled.div<any>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...singleLineClamp,

	color: token('color.text'),
	'&:hover': {
		textDecoration: 'underline',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueKey = styled.span({
	flexShrink: 0,

	color: token('color.text.subtlest'),
	paddingRight: token('space.200'),
});
